/**
 * My Employments — self-service view for any authenticated staff/teacher/etc.
 * Shows their single person record + every active assignment across the ecosystem.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/lib/store';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  User, Briefcase, Calendar, Mail, Phone, CreditCard, Building2, LogOut,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const scopeBadge = (scopeType) => ({
  school: 'bg-indigo-100 text-indigo-700',
  church: 'bg-purple-100 text-purple-700',
  event: 'bg-amber-100 text-amber-700',
  ministry: 'bg-rose-100 text-rose-700',
  org: 'bg-slate-100 text-slate-700',
}[scopeType] || 'bg-slate-100 text-slate-700');

export default function MyEmployments() {
  const navigate = useNavigate();
  const { token, user, logout } = useAuthStore();
  const hdrs = () => ({ Authorization: `Bearer ${token || getToken()}` });

  const { data, isLoading } = useQuery({
    queryKey: ['my-employments'],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/ecosystem-hr/me`, { headers: hdrs() });
      if (!r.ok) throw new Error('Failed to load profile');
      return r.json();
    },
  });

  const person = data?.person;
  const assignments = data?.assignments || [];
  const active = assignments.filter((a) => a.status === 'active');
  const ended = assignments.filter((a) => a.status !== 'active');

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4" data-testid="my-employments-page">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Top bar */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider">Self service</p>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
              <User className="h-7 w-7 text-indigo-500" /> My Employments
            </h1>
          </div>
          <Button variant="outline" size="sm" onClick={() => { logout?.(); navigate('/'); }} data-testid="my-employments-logout">
            <LogOut className="h-4 w-4 mr-1" /> Log out
          </Button>
        </div>

        {/* Profile card */}
        {isLoading ? (
          <Skeleton className="h-48" />
        ) : person ? (
          <Card data-testid="my-employments-profile">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">{person.full_name}</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              {person.email && <div className="flex items-center gap-2 text-slate-700"><Mail className="h-4 w-4 text-slate-400" /> {person.email}</div>}
              {person.phone && <div className="flex items-center gap-2 text-slate-700"><Phone className="h-4 w-4 text-slate-400" /> {person.phone}</div>}
              {person.title && <div className="flex items-center gap-2 text-slate-700"><Briefcase className="h-4 w-4 text-slate-400" /> {person.title}</div>}
              {person.date_of_birth && <div className="flex items-center gap-2 text-slate-700"><Calendar className="h-4 w-4 text-slate-400" /> {person.date_of_birth}</div>}
              {person.national_id && <div className="flex items-center gap-2 text-slate-700"><CreditCard className="h-4 w-4 text-slate-400" /> ID · {person.national_id}</div>}
              {person.nationality && <div className="flex items-center gap-2 text-slate-700"><Building2 className="h-4 w-4 text-slate-400" /> {person.nationality}</div>}
            </CardContent>
          </Card>
        ) : (
          <Card><CardContent className="py-10 text-center text-slate-500">No profile found. Please contact your admin.</CardContent></Card>
        )}

        {/* Active */}
        <Card data-testid="my-employments-active">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-emerald-500" /> Active roles <span className="text-slate-400 text-sm">({active.length})</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {active.length === 0 ? (
              <p className="text-sm text-slate-500">You have no active roles right now.</p>
            ) : active.map((a) => (
              <div key={a._id} className="p-4 rounded-lg border bg-white" data-testid={`assignment-${a._id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold capitalize">{String(a.role).replace(/_/g, ' ')}</p>
                    <p className="text-xs text-slate-500 mt-0.5 capitalize">{a.employment_type}</p>
                  </div>
                  <Badge className={`${scopeBadge(a.scope_type)} capitalize`}>{a.scope_type}</Badge>
                </div>
                {(a.permissions || []).length > 0 && (
                  <div className="flex gap-1 flex-wrap mt-2">
                    {a.permissions.map((p) => <Badge key={p} variant="outline" className="text-xs">{p}</Badge>)}
                  </div>
                )}
                {a.metadata?.subjects?.length > 0 && (
                  <p className="text-xs text-slate-500 mt-2">Subjects: {a.metadata.subjects.join(', ')}</p>
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Ended history */}
        {ended.length > 0 && (
          <Card className="opacity-75">
            <CardHeader className="pb-3"><CardTitle className="text-base">History <span className="text-slate-400 text-sm">({ended.length})</span></CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {ended.map((a) => (
                <div key={a._id} className="p-3 rounded bg-slate-50 text-sm flex items-center justify-between">
                  <span className="capitalize">{String(a.role).replace(/_/g, ' ')}</span>
                  <Badge variant="outline" className="capitalize">{a.status}</Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {user?.role === 'super_admin' && (
          <p className="text-xs text-center text-slate-400">
            Super admin? You can see everyone's data at <button className="underline" onClick={() => navigate('/admin/ecosystem-hr')}>Ecosystem HR</button>.
          </p>
        )}
      </div>
    </div>
  );
}
